/**
 * Which admin page the reviewer has open, derived from the router.
 *
 * The single source of truth for route → context. It mirrors `App.tsx` by hand rather
 * than importing the real route table, because that would drag the lazy page graph into a
 * module that has to stay cheap and testable.
 *
 * This is a prompt HINT, never an authorisation input. The orchestrator BFF still gates
 * on `tabs.applications` and the assistant validates the shape; a forged payload buys
 * exactly what typing an application id into the chat box buys.
 *
 * Pure functions only — no hooks — so it unit-tests without a router.
 */

import { matchPath } from 'react-router';

/**
 * Kinds are the contract with the server's PAGE_KINDS table
 * (`svc-admin-chatbot/src/guardrails/pageContext.ts`): the client picks the key, the
 * server owns the words shown to the model. A kind the server does not know drops the
 * whole payload, so add to both or neither.
 */
export type PageContextKind =
  | 'application_list'
  | 'application_detail'
  | 'doctrine_config'
  | 'providers'
  | 'routing'
  | 'service_onboarding'
  | 'provider_onboarding'
  | 'admin_users'
  | 'admin_groups'
  | 'employer_verification_dashboard'
  | 'employer_verification_analytics'
  | 'income_verification_dashboard';

export interface PageContext {
  kind: PageContextKind;
  application_id?: string;
}

/**
 * A uuid, or nothing.
 *
 * Checked rather than passed through: `/applications/not-a-uuid` should still tell the
 * assistant which screen is open, just without an id it would then fail to look up. The
 * server validates independently — this only avoids sending a value we already know is
 * unusable.
 */
function asApplicationId(raw: string | undefined): string | undefined {
  const text = String(raw ?? '').trim();
  return /^[0-9a-fA-F-]{36}$/.test(text) ? text : undefined;
}

interface RouteRule {
  pattern: string;
  kind: PageContextKind;
  pick?: (params: Record<string, string | undefined>) => Partial<PageContext>;
}

/**
 * Mirrors the reachable routes in `App.tsx`, MOST SPECIFIC FIRST — first match wins.
 * See `.agent-install/PROFILE.md` in svc-admin-chatbot for the audited table.
 *
 * Deliberately absent, and each absence is a decision pinned by a test:
 *  - `/login`, `/mfa/*`, `/identity-verification*` — outside `Layout`, so the panel never
 *    mounts there and no context can exist.
 *  - `/dashboard`, `/audit`, `/admin/audit`, `/doctrines`, `/mocking`, `/service-configs`
 *    — redirect stubs. Matching them would report a page the reviewer is never on.
 *  - `/employer-verification/investigation/:id` and `/employer-verification/company/:id`
 *    — per-record drill-downs rather than areas of the product; the record they show is
 *    better reached through the dashboard that links to it.
 *  - `/no-access` — nothing to ask about.
 */
const ROUTE_RULES: RouteRule[] = [
  {
    pattern: '/applications/:applicationId',
    kind: 'application_detail',
    pick: (p) => ({ application_id: asApplicationId(p.applicationId) }),
  },
  { pattern: '/applications', kind: 'application_list' },
  { pattern: '/doctrine-config/:id', kind: 'doctrine_config' },
  { pattern: '/doctrine-config', kind: 'doctrine_config' },
  { pattern: '/providers/:id', kind: 'providers' },
  { pattern: '/providers', kind: 'providers' },
  { pattern: '/routing', kind: 'routing' },
  { pattern: '/service-onboarding/:serviceId', kind: 'service_onboarding' },
  { pattern: '/service-onboarding', kind: 'service_onboarding' },
  // "/vendors" is labelled "Provider Onboarding" in the sidebar. The kind carries the
  // label's meaning, not the path's, so the assistant names what the reviewer can see.
  { pattern: '/vendors', kind: 'provider_onboarding' },
  // Legacy dashboards linked from the Doctrine configuration shortcut table. Analytics
  // first — '/employer-verification' would otherwise swallow it.
  { pattern: '/employer-verification/analytics', kind: 'employer_verification_analytics' },
  { pattern: '/employer-verification', kind: 'employer_verification_dashboard' },
  { pattern: '/income-verification', kind: 'income_verification_dashboard' },
  { pattern: '/admin/users', kind: 'admin_users' },
  { pattern: '/admin/groups', kind: 'admin_groups' },
];

/** Resolve the open route into a context, or `null` when the route is unmapped. */
export function derivePageContext(pathname: string): PageContext | null {
  for (const rule of ROUTE_RULES) {
    const match = matchPath({ path: rule.pattern, end: true }, pathname);
    if (!match) continue;
    const picked = rule.pick?.(match.params) ?? {};
    // Drop undefined so the payload never carries a key the server must reject, and never
    // sends `"application_id": null`.
    const resolved = Object.fromEntries(
      Object.entries(picked).filter(([, v]) => v !== undefined),
    );
    return { kind: rule.kind, ...resolved } as PageContext;
  }
  return null;
}

/**
 * A stable identity for "which record is open".
 *
 * `kind` plus the ids, and nothing else. Two visits to the same application produce the
 * same key, so a reviewer's decision to detach survives a re-render; two *different*
 * applications produce different keys, so navigating elsewhere re-attaches automatically
 * rather than silently staying detached on a record they never dismissed.
 */
export function pageContextKey(ctx: PageContext): string {
  return [ctx.kind, ctx.application_id ?? ''].join('|');
}

/**
 * The label shown on the context pill — what the reviewer is looking at, in their words.
 *
 * Uses the sidebar vocabulary, not the route: someone on /vendors sees "Provider
 * Onboarding" in the nav, so the pill saying "vendors" would name something they cannot
 * find. Falls back to the kind only if a new one is added without a label, which the test
 * below is there to prevent.
 */
export function describePageContext(ctx: PageContext): string {
  switch (ctx.kind) {
    case 'application_detail':
      return ctx.application_id
        ? `Application ${ctx.application_id.slice(0, 8)}`
        : 'An application';
    case 'application_list':
      return 'Applications';
    case 'doctrine_config':
      return 'Doctrine configuration';
    case 'providers':
      return 'Providers';
    case 'routing':
      return 'Routing';
    case 'service_onboarding':
      return 'Service Onboarding';
    case 'provider_onboarding':
      return 'Provider Onboarding';
    case 'admin_users':
      return 'Users';
    case 'admin_groups':
      return 'Groups';
    case 'employer_verification_dashboard':
      return 'Employer Dashboard';
    case 'employer_verification_analytics':
      return 'EV Analytics';
    case 'income_verification_dashboard':
      return 'Income Verification Dashboard';
  }
}
